import { Stack, Typography } from '@mui/material';
import {
  ChartsLabelMark,
  ChartsTooltipContainer,
  ChartsTooltipPaper,
  useAxesTooltip,
} from '@mui/x-charts';
import type {
  ChartsLabelMarkProps,
  ChartsLabelMarkType,
} from '@mui/x-charts/internals';
import { MONTHS } from '../types';

export function ChartTooltipItem({
  value,
  label,
  color,
  markType,
  slotProps,
}: {
  value: number | string;
  label: string;
  color: string;
  markType: ChartsLabelMarkType;
  slotProps?: {
    labelMark?: ChartsLabelMarkProps;
  };
}) {
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1,
        '&:last-child': {
          pb: 1,
        },
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        <ChartsLabelMark
          color={color}
          type={markType}
          {...slotProps?.labelMark}
          sx={{
            ...(color === 'white' && {
              border: '1px solid',
              borderColor: 'accent.spaceCadet',
            }),
            ...slotProps?.labelMark?.sx,
          }}
        />
        <Typography variant="caption" sx={{ color: 'action.active' }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h6" component="span" sx={{ color: 'action.active' }}>
        {value}
      </Typography>
    </Stack>
  );
}

export function ChartTooltip({ children }: { children: React.ReactNode }) {
  const tooltipData = useAxesTooltip();

  const axisData = tooltipData?.[0];
  const { axisValue: monthValue } = axisData ?? {};

  // ChartsTooltipContainer must always be mounted (never conditionally rendered)
  // so its SVG pointer-event listeners stay active. On mobile, pointerenter fires
  // before the component mounts — if it unmounts between interactions the position
  // resets to (0,0) and the tooltip flashes at the top of the screen on next tap.
  return (
    <ChartsTooltipContainer trigger="axis">
      {axisData != null && children != null ? (
        <ChartsTooltipPaper title="Month">
          <Stack sx={{ gap: 1 }}>
            <Typography
              variant="caption"
              sx={{ mb: 1, px: 1, pt: 1, color: 'action.active' }}
            >
              {typeof monthValue === 'string'
                ? monthValue
                : MONTHS[Number(monthValue)]}
            </Typography>
            {children}
          </Stack>
        </ChartsTooltipPaper>
      ) : null}
    </ChartsTooltipContainer>
  );
}
